import type { IconName } from 'lucide-react/dynamic';
import { DateTime } from 'luxon';

export type EffectType =
  | 'set_price'
  | 'adjust_all_prices_percent'
  | 'adjust_item_price_percent';

export type EffectDurationType = 'end_of_day' | 'duration_minutes';

export interface EffectCardAction {
  type: 'effect';
  effectType: EffectType;
  targetItem?: ShopItem;
  value: number;
  durationType: EffectDurationType;
  durationMinutes?: number;
}

export type CardAction =
  | { type: 'sticker'; item?: ShopItem; amount: number }
  | { type: 'item'; item?: ShopItem; amount: number }
  | EffectCardAction;

export type CardActionPayload =
  | { type: 'sticker'; amount: number }
  | { type: 'item'; item: string; amount: number }
  | {
      type: 'effect';
      effectType: string;
      targetItem?: string;
      value: number;
      durationType: string;
      durationMinutes?: number;
    };

/** Extract total sticker amount from a Card or card reference */
export function getCardStickerAmount(card: Card | string): number {
  if (typeof card === 'string') return 0;
  return card.actions
    .filter(a => a.type === 'sticker')
    .reduce((sum, a) => sum + a.amount, 0);
}

/** Convert CardAction[] to the discriminated union format expected by API payloads */
export function toCardActionPayload(
  actions: CardAction[],
): CardActionPayload[] {
  return actions.map(action => {
    if (action.type === 'item' && action.item) {
      return {
        type: 'item' as const,
        item: typeof action.item === 'string' ? action.item : action.item._id,
        amount: action.amount,
      };
    }
    if (action.type === 'effect') {
      return {
        type: 'effect' as const,
        effectType: action.effectType,
        targetItem: action.targetItem
          ? typeof action.targetItem === 'string'
            ? action.targetItem
            : action.targetItem._id
          : undefined,
        value: action.value,
        durationType: action.durationType,
        durationMinutes: action.durationMinutes,
      };
    }
    return {
      type: 'sticker' as const,
      amount: action.amount,
    };
  });
}

export interface Card {
  _id: string;
  cardType: 'hidden' | 'manual';
  title?: string;
  icon?: string;
  description?: string;
  group: string;
  createdBy: string;
  actions: CardAction[];
  createdAt: string;
  updatedAt: string;
}

export interface CardDelivery {
  _id: string;
  card: Card;
  userId: string;
  deliveredBy: string;
  source: 'task' | 'goal' | 'manual';
  sourceEntityId?: string;
  deliveredAt: string;
}

export interface User {
  _id: string;
  email: string;
  sub: string;
  family_name: string;
  given_name: string;
  picture: string;
  isManager: boolean;
}

export interface ShopItem {
  _id: string;
  title: string;
  description: string;
  price: number;
  icon: IconName;
  effectivePrice?: number;
  purchaseType: 'standard' | 'collectable';
}

export interface ContributionInfo {
  totalCollected: number;
  remaining: number;
  price: number;
  isFullyFunded: boolean;
  contributions: Array<{
    _id: string;
    contributor: User;
    amount: number;
    createdAt: string;
  }>;
}

export interface Effect {
  _id: string;
  groupId: string;
  userId: string;
  effectType: EffectType;
  value: number;
  targetItem?: ShopItem;
  expiresAt: DateTime;
  createdAt: DateTime;
  updatedAt: DateTime;
}

export interface RecurrenceConfig {
  _id?: string;
  frequency: 'day' | 'week' | 'month' | 'year';
  interval: number;
  byWeekday?: number[];
  byMonthDay?: number[];
  byWeekNo?: number[];
  bySetPos?: number[];
  until?: DateTime;
  count?: number;
  tz: string;
  durationMinutes: number;
  dtStart: DateTime;
}

export interface Recurrence<TAssignedTo = User[]> {
  _id: string;
  title: string;
  description?: string;
  createdBy: string;
  assignedTo: TAssignedTo;
  rewardCard: Card | string;
  penaltyCard?: Card | string;
  config: RecurrenceConfig;
  icon: IconName;
  createdAt: DateTime;
  updatedAt: DateTime;
}

export const RecurrenceTaskStatusEnum = {
  InProgress: 'InProgress',
  CompleteRequest: 'CompleteRequest',
  Canceled: 'Canceled',
  Succeed: 'Succeed',
  Failed: 'Failed',
} as const;

export type RecurrenceTaskStatus =
  (typeof RecurrenceTaskStatusEnum)[keyof typeof RecurrenceTaskStatusEnum];

export interface RecurrenceTaskItem {
  _id: string;
  isCompleted: boolean;
  status: RecurrenceTaskStatus;
  start: DateTime;
  stop: DateTime;
}

export interface Group {
  _id: string;
  title: string;
  managers: User[];
  managed: User[];
  createdAt: DateTime;
  updatedAt: DateTime;
}

export interface Purchase {
  _id: string;
  requestedBy: User;
  item: ShopItem;
  quantity: number;
  totalAmount: number;
  status: 'Pending' | 'Approved' | 'Rejected';
  transaction?: string;
  processedBy?: User;
  processedAt?: DateTime;
  rejectionReason?: string;
  createdAt: DateTime;
  updatedAt: DateTime;
}

export interface TransactionParticipant {
  _id: string;
  email: string;
  given_name: string;
  family_name: string;
  picture?: string;
  sub?: string;
  isManager?: boolean;
  createdAt?: DateTime;
  updatedAt?: DateTime;
}

export interface UserTransaction {
  _id: string;
  source: TransactionParticipant;
  destination: TransactionParticipant;
  amount: number;
  description: string;
  createdAt: DateTime;
  updatedAt: DateTime;
}

/** Notification types matching backend enum */
export type NotificationType =
  | 'card_delivered'
  | 'effect_activated'
  | 'purchase_rejected'
  | 'stickers_changed'
  | 'achievement_reached'
  | 'task_failed';

/** Notification status matching backend enum */
export type NotificationStatus = 'new' | 'seen' | 'hidden';

/** In-app notification for managed users */
export interface UserNotification {
  _id: string;
  userId: string;
  type: NotificationType;
  params: Record<string, string>;
  status: NotificationStatus;
  createdAt: DateTime;
  updatedAt: DateTime;
}
